/*! @wharfkit/wallet-plugin-anchor v4.0.2 | BSD-3-Clause | Copyright (c) 2023 Greymass Inc. | https://github.com/wharfkit/js */
import { AbstractWalletPlugin, Checksum256, Logo, PermissionLevel, PrivateKey, PublicKey, ResolvedSigningRequest, Serializer, WalletPluginMetadata } from "@wharfkit/session";
import { LinkInfo, clearTransactionHandoff, createIdentityRequest, extractSignaturesFromCallback, generateReturnUrl, isAppleHandheld, isCallback, isKnownMobile, isSamePageReturn, sealMessage, setTransactionCallback, storeTransactionHandoff, verifyLoginCallbackResponse, waitForCallback, waitForPageReturn } from "@wharfkit/protocol-esr";
import { send } from "@greymass/buoy";
import { PrivateKey as PrivateKey$1, PublicKey as PublicKey$1, UInt64 } from "@wharfkit/antelope";
//#endregion
//#region src/translations/index.ts
var translations_default = {
	en: {
		login: {
			"title": "Connect with Anchor",
			"body": "Scan with Anchor on your mobile device or click the button below to open on this device.",
			"link": "Launch Anchor"
		},
		mode: {
			"title": "How do you use Anchor?",
			"body": "Choose where to approve requests. You can choose differently the next time you log in.",
			"web": {
				"label": "In this browser using anchorwallet.io",
				"detail": "with a passkey or Ledger",
				"detail_no_ledger": "with a passkey"
			},
			"app": {
				"label": "With the Anchor app",
				"detail": "on this or another device"
			},
			"switch": {
				"to_web": "Use Anchor in this browser instead",
				"to_app": "Use the Anchor app instead"
			}
		},
		fallback: {
			"body": "Don't have the app?",
			"label": "Continue in this browser instead"
		},
		recovery: {
			"title": "Request cancelled",
			"body": "Choose how you want to try again."
		},
		web: {
			"waiting": {
				"title": "Approve in Anchor",
				"body": "Please approve this in the Anchor window."
			},
			"blocked": {
				"title": "Pop-up blocked",
				"body": "Pop-up blocked by your browser. Open the Anchor window manually.",
				"label": "Open Anchor"
			}
		},
		transact: {
			"title": "Complete using Anchor",
			"body": "Please open your Anchor Wallet on \"{{channelName}}\" to review and approve this transaction.",
			"label": "Sign manually or with another device",
			"link": "Trigger Manually",
			"await": "Waiting for response from Anchor"
		},
		error: {
			"expired": "The request expired, please try again.",
			"invalid_response": "Invalid response from Anchor, must contain link_ch, link_key, link_name and cid flags.",
			"not_completed": "The request was not completed.",
			"cancelled": "The request was cancelled from Anchor."
		}
	},
	ko: {
		login: {
			"title": "Anchor로 연결",
			"body": "모바일 기기에서 Anchor로 스캔하거나 아래 버튼을 클릭하여 이 기기에서 여세요.",
			"link": "Anchor 실행"
		},
		mode: {
			"title": "Anchor를 어떻게 사용하시나요?",
			"body": "요청을 승인할 위치를 선택하세요. 다음 로그인 시 다른 방법을 선택할 수 있습니다.",
			"web": {
				"label": "이 브라우저에서 anchorwallet.io 사용",
				"detail": "패스키 또는 Ledger 사용",
				"detail_no_ledger": "패스키 사용"
			},
			"app": {
				"label": "Anchor 앱으로",
				"detail": "이 기기 또는 다른 기기에서"
			},
			"switch": {
				"to_web": "대신 이 브라우저에서 Anchor 사용",
				"to_app": "대신 Anchor 앱 사용"
			}
		},
		fallback: {
			"body": "Anchor 앱이 없으신가요?",
			"label": "대신 이 브라우저에서 계속"
		},
		recovery: {
			"title": "요청이 취소되었습니다",
			"body": "다시 시도할 방법을 선택하세요."
		},
		web: {
			"waiting": {
				"title": "Anchor에서 승인",
				"body": "Anchor 창에서 승인해 주세요."
			},
			"blocked": {
				"title": "팝업이 차단됨",
				"body": "브라우저에서 팝업을 차단했습니다. Anchor 창을 직접 열어 주세요.",
				"label": "Anchor 열기"
			}
		},
		transact: {
			"title": "Anchor로 완료",
			"body": "이 거래를 검토하고 승인하려면 \"{{channelName}}\"에서 Anchor 지갑을 여세요.",
			"label": "수동으로 또는 다른 기기로 서명",
			"link": "수동으로 실행",
			"await": "Anchor의 응답을 기다리는 중"
		},
		error: {
			"expired": "요청이 만료되었습니다. 다시 시도해 주세요.",
			"invalid_response": "Anchor의 응답이 유효하지 않습니다. link_ch, link_key, link_name 및 cid 플래그가 포함되어야 합니다.",
			"not_completed": "요청이 완료되지 않았습니다.",
			"cancelled": "요청이 Anchor에서 취소되었습니다."
		}
	},
	"zh-Hans": {
		login: {
			"link": "启动Anchor",
			"body": "在您的设备上使用Anchor扫描或者点击下方按钮打开。",
			"title": "连接Anchor"
		},
		mode: {
			"title": "您如何使用 Anchor？",
			"body": "选择批准请求的位置。下次登录时，您可以选择其他方式。",
			"web": {
				"label": "在此浏览器中使用 anchorwallet.io",
				"detail": "使用通行密钥或 Ledger",
				"detail_no_ledger": "使用通行密钥"
			},
			"app": {
				"label": "使用 Anchor 应用",
				"detail": "在此设备或其他设备上"
			},
			"switch": {
				"to_web": "改在此浏览器中使用 Anchor",
				"to_app": "改用 Anchor 应用"
			}
		},
		fallback: {
			"body": "没有安装 Anchor 应用？",
			"label": "改在此浏览器中继续"
		},
		recovery: {
			"title": "请求已取消",
			"body": "请选择重新尝试的方式。"
		},
		web: {
			"waiting": {
				"title": "在 Anchor 中批准",
				"body": "请在 Anchor 窗口中批准。"
			},
			"blocked": {
				"title": "弹出窗口已被拦截",
				"body": "浏览器拦截了弹出窗口。请手动打开 Anchor 窗口。",
				"label": "打开 Anchor"
			}
		},
		error: {
			"cancelled": "请求已从Anchor取消。",
			"not_completed": "此请求未完成。",
			"invalid_response": "无效的Anchor响应，必须包含link_ch, link_key, link_name和cid标识符。",
			"expired": "请求已过期，请重试。"
		},
		transact: {
			"await": "等待Anchor响应",
			"link": "手动触发",
			"label": "手动或使用其他设备签约",
			"body": "请在\"{{channelName}}\"上打开您的Anchor钱包以浏览并批准此交易。",
			"title": "完成使用Anchor"
		}
	},
	"zh-Hant": {
		login: {
			"title": "連接Anchor",
			"body": "在您的裝置上使用Anchor掃描或者點擊下方按鈕開啟。",
			"link": "啟動Anchor"
		},
		mode: {
			"title": "您要如何使用 Anchor？",
			"body": "選擇批准請求的位置。下次登入時，您可以選擇其他方式。",
			"web": {
				"label": "在此瀏覽器中使用 anchorwallet.io",
				"detail": "使用通行密鑰或 Ledger",
				"detail_no_ledger": "使用通行密鑰"
			},
			"app": {
				"label": "使用 Anchor 應用程式",
				"detail": "在此裝置或其他裝置上"
			},
			"switch": {
				"to_web": "改在此瀏覽器中使用 Anchor",
				"to_app": "改用 Anchor 應用程式"
			}
		},
		fallback: {
			"body": "沒有安裝 Anchor 應用程式？",
			"label": "改在此瀏覽器中繼續"
		},
		recovery: {
			"title": "請求已取消",
			"body": "請選擇重新嘗試的方式。"
		},
		web: {
			"waiting": {
				"title": "在 Anchor 中批准",
				"body": "請在 Anchor 視窗中批准。"
			},
			"blocked": {
				"title": "彈出式視窗已遭封鎖",
				"body": "瀏覽器封鎖了彈出式視窗。請手動開啟 Anchor 視窗。",
				"label": "開啟 Anchor"
			}
		},
		transact: {
			"title": "完成使用Anchor",
			"body": "請在\"{{channelName}}\"上開啟您的Anchor錢包以瀏覽並批准此交易。",
			"label": "手動或使用其他裝置簽署",
			"link": "手動觸發",
			"await": "等待Anchor回應"
		},
		error: {
			"expired": "請求已過期，請重試。",
			"invalid_response": "無效的Anchor回應，必須包含link_ch, link_key, link_name和cid標識符。",
			"not_completed": "此請求未完成。",
			"cancelled": "請求已從Anchor取消。"
		}
	}
};
//#endregion
//#region src/chains.ts
const DEFAULT_WEB_AUTHENTICATOR_URLS = {
	aca376f206b8fc25a6ed44dbdc66547c36c6c33e3a119ffbeaef943642f0e906: "https://vaulta.anchorwallet.io",
	"73e4385a2708e6d7048834fbc1079f2fabb17b3c125b146af438971e90716c4d": "https://jungle4.anchorwallet.io"
};
function findUrl(urls, chainId) {
	const match = Object.keys(urls).find((key) => key.toLowerCase() === chainId);
	return match ? urls[match].replace(/\/+$/, "") : void 0;
}
function resolveWebAuthenticatorUrl(chainId, overrides = {}) {
	if (!chainId) return;
	const key = String(chainId).toLowerCase();
	return findUrl(overrides, key) ?? findUrl(DEFAULT_WEB_AUTHENTICATOR_URLS, key);
}
//#endregion
//#region src/mode.ts
function isValidMode(value) {
	return value === "web" || value === "app";
}
function readMode(data) {
	if (isValidMode(data.mode)) return data.mode;
	if (data.channelUrl || data.signerKey) return "app";
	if (data.encryptionKey && data.messageKey) return "web";
}
function writeMode(data, mode) {
	if (!isValidMode(mode)) throw new Error(`Invalid Anchor mode: ${mode}`);
	data.mode = mode;
}
function readLoginOptions(id, arbitrary) {
	const options = arbitrary?.[id];
	if (options === void 0 || options === null) return {};
	if (typeof options !== "object") throw new Error(`Invalid Anchor login options: ${options}`);
	const result = {};
	if (options.mode !== void 0) {
		if (!isValidMode(options.mode)) throw new Error(`Invalid Anchor mode: ${options.mode}`);
		result.mode = options.mode;
	}
	if (options.popup) result.popup = options.popup;
	return result;
}
function ledgerTransportAvailable() {
	if (typeof navigator === "undefined") return false;
	const nav = navigator;
	return Boolean(nav.hid) || Boolean(nav.usb);
}
function modeElements(t, onChoice, includeWeb) {
	const webLabel = t("mode.web.label", { default: "In this browser using anchorwallet.io" });
	const webDetail = ledgerTransportAvailable() ? t("mode.web.detail", { default: "with a passkey or Ledger" }) : t("mode.web.detail_no_ledger", { default: "with a passkey" });
	const appLabel = t("mode.app.label", { default: "With the Anchor app" });
	const appDetail = t("mode.app.detail", { default: "on this or another device" });
	const elements = [];
	if (includeWeb) elements.push({
		type: "button",
		label: webLabel,
		data: {
			label: `${webLabel}\n${webDetail}`,
			variant: "primary",
			onClick: () => onChoice("web")
		}
	});
	elements.push({
		type: "button",
		label: appLabel,
		data: {
			label: `${appLabel}\n${appDetail}`,
			variant: includeWeb ? "secondary" : "primary",
			onClick: () => onChoice("app")
		}
	});
	return elements;
}
function promptForMode(context, t, onChoice) {
	if (!context.ui) throw new Error("No UI available");
	return context.ui.prompt({
		title: t("mode.title", { default: "How do you use Anchor?" }),
		body: t("mode.body", { default: "Choose where to approve requests. You can choose differently the next time you log in." }),
		elements: modeElements(t, onChoice, true)
	});
}
function promptForRecovery(context, t, includeWeb, onChoice) {
	if (!context.ui) throw new Error("No UI available");
	return context.ui.prompt({
		title: t("recovery.title", { default: "Request cancelled" }),
		body: t("recovery.body", { default: "Choose how you want to try again." }),
		elements: modeElements(t, onChoice, includeWeb)
	});
}
//#endregion
//#region src/transports/errors.ts
var AnchorRequestCancelledError = class extends Error {
	constructor(message = "The request was cancelled.") {
		super(message);
		this.name = "AnchorRequestCancelledError";
	}
};
//#endregion
//#region src/transports/prompt.ts
function switchButton(label, variant, onSelect) {
	return {
		type: "button",
		label,
		data: {
			label,
			variant,
			onClick: () => onSelect()
		}
	};
}
//#endregion
//#region src/transports/native.ts
var NativeTransport = class {
	constructor(options) {
		this.options = options;
	}
	async login(data, context, bundle, t, fallback) {
		if (!context.ui) throw new Error("No UI available");
		const { callback, request, sameDeviceRequest, requestKey, privateKey } = bundle;
		const elements = [{
			type: "link",
			label: t("login.link", { default: "Launch Anchor" }),
			data: {
				href: sameDeviceRequest.encode(true, false, "esr:"),
				label: t("login.link", { default: "Launch Anchor" }),
				variant: "primary"
			}
		}];
		if (!isKnownMobile()) elements.unshift({
			type: "qr",
			data: request.encode(true, false, "esr:")
		});
		if (fallback?.immediate) elements.push(switchButton(t("mode.switch.to_web", { default: "Use Anchor in this browser instead" }), "text", () => fallback.onSelect()));
		window.location.href = sameDeviceRequest.encode(true, false, "esr:");
		context.ui.prompt({
			title: t("login.title", { default: "Connect with Anchor" }),
			body: t("login.body", { default: "Scan with Anchor on your mobile device or click the button below to open on this device." }),
			elements
		}).catch(() => {
			console.info("Modal closed");
		});
		const fallbackTimer = fallback ? setTimeout(() => {
			context.ui.prompt({
				title: t("login.title", { default: "Connect with Anchor" }),
				body: t("fallback.body", { default: "Don't have the app?" }),
				elements: [...elements.filter((element) => element.type !== "button"), switchButton(t("fallback.label", { default: "Continue in this browser instead" }), "secondary", () => fallback.onSelect())]
			}).catch(() => void 0);
		}, fallback.delayMs) : void 0;
		const cancelledMessage = t("error.cancelled", { default: "The request was cancelled from Anchor." });
		try {
			const callbackResponse = await (this.options.waitForCallback ?? waitForCallback)(callback, this.options.buoyWs, t);
			verifyLoginCallbackResponse(callbackResponse, context);
			if (!callbackResponse.cid || !callbackResponse.sa || !callbackResponse.sp) throw new Error("Invalid callback response");
			if (callbackResponse.link_ch && callbackResponse.link_key && callbackResponse.link_name) {
				data.requestKey = requestKey;
				data.privateKey = privateKey;
				data.signerKey = PublicKey.from(callbackResponse.link_key);
				data.channelUrl = callbackResponse.link_ch;
				data.channelName = callbackResponse.link_name;
				try {
					if (callbackResponse.link_meta) {
						const metadata = JSON.parse(callbackResponse.link_meta);
						data.sameDevice = metadata.sameDevice;
						data.launchUrl = metadata.launchUrl;
						data.triggerUrl = metadata.triggerUrl;
					}
				} catch {}
			}
			const resolvedResponse = await ResolvedSigningRequest.fromPayload(callbackResponse, context.esrOptions);
			return {
				chain: Checksum256.from(callbackResponse.cid),
				permissionLevel: PermissionLevel.from({
					actor: callbackResponse.sa,
					permission: callbackResponse.sp
				}),
				identityProof: resolvedResponse.getIdentityProof(callbackResponse.sig)
			};
		} catch (error) {
			if (error instanceof Error && error.message === cancelledMessage) throw new AnchorRequestCancelledError(error.message);
			throw error;
		} finally {
			if (fallbackTimer) clearTimeout(fallbackTimer);
		}
	}
	async sign(data, resolved, context) {
		if (!context.ui) throw new Error("No UI available");
		const t = context.ui.getTranslate(this.options.id);
		const expiration = resolved.transaction.expiration.toDate();
		const now = new Date();
		const expiresIn = Math.floor(expiration.getTime() - now.getTime());
		const modifiedRequest = await context.createRequest({ transaction: resolved.transaction });
		modifiedRequest.setInfoKey("link", LinkInfo.from({ expiration }));
		const callback = setTransactionCallback(modifiedRequest, this.options.buoyUrl);
		const request = modifiedRequest.encode(true, false);
		const isSameDevice = data.sameDevice !== false;
		const sameDeviceRequest = modifiedRequest.clone();
		const returnUrl = (this.options.generateReturnUrl ?? generateReturnUrl)();
		sameDeviceRequest.setInfoKey("same_device", true);
		if (returnUrl) sameDeviceRequest.setInfoKey("return_path", returnUrl);
		const signManually = () => {
			context.ui?.prompt({
				title: t("transact.sign_manually.title", { default: "Sign manually" }),
				body: t("transact.sign_manually.body", { default: "Scan the QR-code with Anchor on another device or use the button to open it here." }),
				elements: [{
					type: "qr",
					data: String(request)
				}, {
					type: "link",
					label: t("transact.sign_manually.link.title", { default: "Open Anchor" }),
					data: {
						href: String(sameDeviceRequest),
						label: t("transact.sign_manually.link.title", { default: "Open Anchor" })
					}
				}]
			});
		};
		const promptPromise = context.ui.prompt({
			title: t("transact.title", { default: "Complete using Anchor" }),
			body: t("transact.body", {
				channelName: data.channelName,
				default: `Please open your Anchor Wallet on "${data.channelName}" to review and approve this transaction.`
			}),
			elements: [{
				type: "countdown",
				data: {
					label: t("transact.await", { default: "Waiting for response from Anchor" }),
					end: expiration.toISOString()
				}
			}, {
				type: "button",
				label: t("transact.label", { default: "Sign manually or with another device" }),
				data: {
					onClick: isSameDevice ? () => window.location.href = sameDeviceRequest.encode() : signManually,
					label: t("transact.label", { default: "Sign manually or with another device" })
				}
			}]
		});
		const timer = setTimeout(() => {
			promptPromise.cancel(t("error.expired", { default: "The request expired, please try again." }));
		}, expiresIn);
		promptPromise.catch(() => clearTimeout(timer));
		const callbackController = new AbortController();
		const deferCallbackUntilReturn = data.sameDevice && returnUrl && isSamePageReturn(returnUrl);
		const transactionHandoff = deferCallbackUntilReturn && returnUrl ? {
			version: 1,
			returnUrl,
			callback,
			transactionId: String(resolved.transaction.id),
			chainId: String(context.chain.id),
			actor: String(context.permissionLevel.actor),
			permission: String(context.permissionLevel.permission),
			expiresAt: expiration.toISOString()
		} : null;
		if (transactionHandoff) storeTransactionHandoff(transactionHandoff);
		const callbackPromise = deferCallbackUntilReturn ? waitForPageReturn(returnUrl, callbackController.signal).then(() => (this.options.waitForCallback ?? waitForCallback)(callback, this.options.buoyWs, t)) : (this.options.waitForCallback ?? waitForCallback)(callback, this.options.buoyWs, t);
		try {
			if (data.channelUrl) {
				const service = new URL(data.channelUrl).origin;
				const channel = new URL(data.channelUrl).pathname.substring(1);
				const sealedMessage = await sealMessage((data.sameDevice ? sameDeviceRequest : modifiedRequest).encode(true, false, "esr:"), PrivateKey.from(data.privateKey), PublicKey.from(data.signerKey));
				const payload = Serializer.encode({ object: sealedMessage }).array;
				if (data.sameDevice) {
					await (this.options.send ?? send)(payload, {
						service,
						channel
					});
					if (data.launchUrl) window.location.href = data.launchUrl;
					else if (isAppleHandheld()) window.location.href = "anchor://link";
				} else (this.options.send ?? send)(payload, {
					service,
					channel
				});
			} else window.location.href = sameDeviceRequest.encode();
		} catch (error) {
			clearTimeout(timer);
			callbackPromise.catch(() => void 0);
			callbackController.abort();
			promptPromise.cancel();
			throw error;
		}
		const callbackResponse = await Promise.race([callbackPromise, promptPromise]).finally(() => {
			clearTimeout(timer);
			callbackController.abort();
			promptPromise.cancel();
			if (transactionHandoff) clearTransactionHandoff(transactionHandoff);
		});
		if (isCallback(callbackResponse) && extractSignaturesFromCallback(callbackResponse).length > 0) {
			const resolvedRequest = await ResolvedSigningRequest.fromPayload(callbackResponse, context.esrOptions);
			return {
				signatures: extractSignaturesFromCallback(callbackResponse),
				resolved: resolvedRequest
			};
		}
		const errorString = t("error.not_completed", { default: "The request was not completed." });
		promptPromise.cancel(errorString);
		throw new Error(errorString);
	}
};
//#endregion
//#region src/transports/web.ts
const POPUP_WIDTH = 450;
const POPUP_HEIGHT = 750;
function signPageUrl(baseUrl, params) {
	return `${baseUrl}/sign?${Object.entries(params).map(([key, value]) => `${key}=${value}`).join("&")}`;
}
function openAuthenticatorWindow(url) {
	const left = Math.round(window.screenX + (window.outerWidth - POPUP_WIDTH) / 2);
	const top = Math.round(window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2);
	return window.open(url, "anchor-web", `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`);
}
var WebTransport = class {
	constructor(options) {
		this.options = options;
	}
	openWindow(url) {
		return openAuthenticatorWindow(url);
	}
	loginUrl(context, bundle, baseUrl) {
		return signPageUrl(baseUrl, {
			esr: String(bundle.request.encode()),
			chain: String(context.chain?.id),
			requestKey: String(bundle.requestKey)
		});
	}
	async login(data, context, bundle, baseUrl, popup, switchOffer) {
		context.appName = context.appName || "Unknown App";
		const url = this.loginUrl(context, bundle, baseUrl);
		const { payload } = await this.awaitPopup(url, bundle.callback, context.ui, popup === void 0 ? this.openWindow(url) : popup, switchOffer);
		data.encryptionKey = String(bundle.privateKey);
		data.messageKey = payload.link_key;
		if (!payload.cid) throw new Error("Login failed: No chain ID returned");
		const loginResponse = {
			chain: Checksum256.from(payload.cid),
			permissionLevel: PermissionLevel.from({
				actor: payload.sa,
				permission: payload.sp
			})
		};
		if (payload.sig) Object.assign(loginResponse, { identityProof: {
			signature: payload.sig,
			signedRequest: bundle.request.encode()
		} });
		return loginResponse;
	}
	async sign(data, resolved, context, baseUrl) {
		if (!data.encryptionKey || !data.messageKey) throw new Error("No request keys available - please login first");
		const expiration = resolved.transaction.expiration.toDate();
		const modifiedRequest = await context.createRequest({ transaction: resolved.transaction });
		modifiedRequest.setInfoKey("link", LinkInfo.from({ expiration }));
		const callback = setTransactionCallback(modifiedRequest, this.options.buoyUrl);
		const nonce = UInt64.from(Math.floor(Math.random() * Number.MAX_SAFE_INTEGER));
		const signUrl = signPageUrl(baseUrl, {
			sealed: (await sealMessage(modifiedRequest.encode(), PrivateKey$1.from(data.encryptionKey), PublicKey$1.from(data.messageKey), nonce)).ciphertext.toString("hex"),
			nonce: nonce.toString(),
			chain: String(context.chain?.id),
			accountName: String(context.accountName),
			permissionName: String(context.permissionName),
			appName: String(context.appName),
			requestKey: String(PrivateKey$1.from(data.encryptionKey).toPublic())
		});
		const response = await this.awaitPopup(signUrl, callback, context.ui, this.openWindow(signUrl));
		const signatures = extractSignaturesFromCallback(response.payload);
		if (!isCallback(response.payload) || signatures.length === 0) throw new Error("Signing failed: No signatures returned");
		return {
			signatures,
			resolved
		};
	}
	awaitPopup(url, receiveOptions, ui, popup, switchOffer) {
		return new Promise((resolve, reject) => {
			const t = ui?.getTranslate(this.options.id);
			const translate = (key, fallbackText) => t ? t(key, { default: fallbackText }) : fallbackText;
			const cancelledMessage = translate("error.cancelled", "The request was cancelled from Anchor.");
			if (!popup) return this.promptManualOpen(url, receiveOptions, ui).then(resolve, reject);
			const elements = [];
			if (switchOffer) {
				const label = translate("mode.switch.to_app", "Use the Anchor app instead");
				elements.push(switchButton(label, "text", () => switchOffer.onSelect()));
			}
			ui?.prompt({
				title: translate("web.waiting.title", "Approve in Anchor"),
				body: translate("web.waiting.body", "Please approve this in the Anchor window."),
				elements
			}).catch(() => void 0);
			const checkClosedInterval = setInterval(() => {
				if (popup.closed) {
					clearInterval(checkClosedInterval);
					reject(new AnchorRequestCancelledError());
				}
			}, 1e3);
			(this.options.waitForCallback ?? waitForCallback)(receiveOptions, this.options.buoyWs, t).then((response) => {
				popup.close();
				resolve({ payload: response });
			}).catch((error) => {
				popup.close();
				if (error instanceof Error && error.message === cancelledMessage) reject(new AnchorRequestCancelledError(error.message));
				else reject(error);
			}).finally(() => {
				clearInterval(checkClosedInterval);
			});
		});
	}
	promptManualOpen(url, receiveOptions, ui) {
		return new Promise((resolve, reject) => {
			const t = ui?.getTranslate(this.options.id);
			const translate = (key, fallbackText) => t ? t(key, { default: fallbackText }) : fallbackText;
			const cancelledMessage = translate("error.cancelled", "The request was cancelled from Anchor.");
			const openLabel = translate("web.blocked.label", "Open Anchor");
			ui?.prompt({
				title: translate("web.blocked.title", "Pop-up blocked"),
				body: translate("web.blocked.body", "Pop-up blocked by your browser. Open the Anchor window manually."),
				elements: [{
					type: "link",
					label: openLabel,
					data: {
						href: url,
						label: openLabel,
						variant: "primary"
					}
				}]
			}).catch(reject);
			(this.options.waitForCallback ?? waitForCallback)(receiveOptions, this.options.buoyWs, t).then((response) => resolve({ payload: response })).catch((error) => {
				if (error instanceof Error && error.message === cancelledMessage) reject(new AnchorRequestCancelledError(error.message));
				else reject(error);
			});
		});
	}
};
//#endregion
//#region src/index.ts
const DEFAULT_WEB_FALLBACK_DELAY_MS = 8e3;
var WalletPluginAnchor = class WalletPluginAnchor extends AbstractWalletPlugin {
	constructor(options) {
		super();
		this.id = "anchor";
		this.translations = translations_default;
		this.config = {
			requiresChainSelect: false,
			requiresPermissionSelect: false
		};
		this.metadata = WalletPluginMetadata.from({
			name: "Anchor",
			description: "",
			logo: Logo.from({
				dark: "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPHN2ZyB3aWR0aD0iMTYwIiBoZWlnaHQ9IjE2MCIgdmlld0JveD0iMCAwIDI1NiAyNTYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPGcgdHJhbnNmb3JtPSJtYXRyaXgoMS40NCwgMCwgMCwgMS40NCwgLTguNTAxOTI1LCAtNTcuMDc0NTcpIiBzdHlsZT0iIj4KICAgIDx0aXRsZT5XaGl0ZTwvdGl0bGU+CiAgICA8Y2lyY2xlIGN4PSI5NC43OTMiIGN5PSIxMjguNTI0IiByPSI4MCIgZmlsbD0iI0ZCRkRGRiIvPgogICAgPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0gOTQuNzk5IDc4LjUyNCBDIDk3LjA5OCA3OC41MjQgOTkuMTk1IDc5LjgzNyAxMDAuMTk4IDgxLjkwNiBMIDEyNC4yMDQgMTMxLjQwNiBMIDEyNC43NDYgMTMyLjUyNCBMIDExMS40MDkgMTMyLjUyNCBMIDEwNy41MyAxMjQuNTI0IEwgODIuMDY5IDEyNC41MjQgTCA3OC4xODkgMTMyLjUyNCBMIDY0Ljg1MyAxMzIuNTI0IEwgNjUuMzk1IDEzMS40MDYgTCA4OS40MDEgODEuOTA2IEMgOTAuNDA0IDc5LjgzNyA5Mi41MDEgNzguNTI0IDk0Ljc5OSA3OC41MjQgWiBNIDg2LjkxOSAxMTQuNTI0IEwgMTAyLjY4IDExNC41MjQgTCA5NC43OTkgOTguMjc0IEwgODYuOTE5IDExNC41MjQgWiBNIDExMi43OTMgMTQ5LjUyNCBMIDEyNC43OTggMTQ5LjUyNCBDIDEyNC40MzcgMTY1LjY3NiAxMTEuMDY3IDE3OC41MjQgOTQuNzk5IDE3OC41MjQgQyA3OC41MzIgMTc4LjUyNCA2NS4xNjIgMTY1LjY3NiA2NC44MDEgMTQ5LjUyNCBMIDc2LjgwNiAxNDkuNTI0IEMgNzcuMDg3IDE1Ni44NzggODEuOTc0IDE2My4xNTUgODguNzkzIDE2NS41MiBMIDg4Ljc5MyAxNDEuNTI0IEMgODguNzkzIDEzOC4yMSA5MS40OCAxMzUuNTI0IDk0Ljc5MyAxMzUuNTI0IEMgOTguMTA3IDEzNS41MjQgMTAwLjc5MyAxMzguMjEgMTAwLjc5MyAxNDEuNTI0IEwgMTAwLjc5MyAxNjUuNTI0IEMgMTA3LjYyIDE2My4xNjIgMTEyLjUxMSAxNTYuODgzIDExMi43OTMgMTQ5LjUyNCBaIiBmaWxsPSIjMzY1MEEyIi8+CiAgPC9nPgo8L3N2Zz4=",
				light: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYwIiBoZWlnaHQ9IjE2MCIgdmlld0JveD0iMCAwIDE2MCAxNjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxjaXJjbGUgY3g9IjgwIiBjeT0iODAiIHI9IjgwIiBmaWxsPSIjMzY1MEEyIi8+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNODAuMDA2MyAzMEM4Mi4zMDUxIDMwIDg0LjQwMTkgMzEuMzEzNCA4NS40MDUgMzMuMzgxOEwxMDkuNDExIDgyLjg4MjJMMTA5Ljk1MyA4NEg5Ni42MTYzTDkyLjczNjYgNzZINjcuMjc1OUw2My4zOTYxIDg0SDUwLjA1OTRMNTAuNjAxNyA4Mi44ODE4TDc0LjYwNzcgMzMuMzgxOEM3NS42MTA4IDMxLjMxMzQgNzcuNzA3NSAzMCA4MC4wMDYzIDMwWk03Mi4xMjU2IDY2SDg3Ljg4N0w4MC4wMDYzIDQ5Ljc1MDFMNzIuMTI1NiA2NlpNOTcuOTk5NSAxMDFIMTEwLjAwNUMxMDkuNjQ0IDExNy4xNTIgOTYuMjczOCAxMzAgODAuMDA2MyAxMzBDNjMuNzM4OCAxMzAgNTAuMzY4NiAxMTcuMTUyIDUwLjAwNzggMTAxSDYyLjAxMzFDNjIuMjk0MSAxMDguMzU0IDY3LjE4MDQgMTE0LjYzMSA3NC4wMDAzIDExNi45OTZWOTNDNzQuMDAwMyA4OS42ODYzIDc2LjY4NjYgODcgODAuMDAwMyA4N0M4My4zMTQgODcgODYuMDAwMyA4OS42ODYzIDg2LjAwMDMgOTNWMTE3QzkyLjgyNjUgMTE0LjYzOCA5Ny43MTgzIDEwOC4zNTkgOTcuOTk5NSAxMDFaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K"
			}),
			homepage: "https://anchorwallet.io",
			download: "https://anchorwallet.io/download"
		});
		this.buoyUrl = options?.buoyUrl || "https://cb.anchor.link";
		this.buoyWs = options?.buoyWs;
		this.webAuthenticatorUrls = options?.webAuthenticatorUrls || {};
		this.webFallbackDelayMs = options?.webFallbackDelayMs ?? 8e3;
		const transportOptions = {
			id: this.id,
			buoyUrl: this.buoyUrl,
			buoyWs: this.buoyWs,
			...options?.transport
		};
		this.native = new NativeTransport(transportOptions);
		this.web = new WebTransport(transportOptions);
		this.setMode(options?.mode);
	}
	login(context) {
		return this.handleLogin(context);
	}
	setMode(mode) {
		if (mode === void 0) {
			this.loginModeOverride = void 0;
			delete this.data.mode;
			return;
		}
		writeMode(this.data, mode);
		this.loginModeOverride = mode;
	}
	getMode() {
		return readMode(this.data);
	}
	openWallet(chain) {
		return WalletPluginAnchor.openWallet(chain, this.webAuthenticatorUrls);
	}
	static openWallet(chain, webAuthenticatorUrls) {
		const url = resolveWebAuthenticatorUrl(chain && typeof chain === "object" && "id" in chain ? chain.id : chain, webAuthenticatorUrls);
		if (!url) return null;
		return openAuthenticatorWindow(url);
	}
	async handleLogin(context) {
		if (!context.ui) throw new Error("No UI available");
		const perCall = readLoginOptions(this.id, context.arbitrary);
		const preopened = perCall.popup;
		const t = context.ui.getTranslate(this.id);
		const webUrl = resolveWebAuthenticatorUrl(context.chain?.id, this.webAuthenticatorUrls);
		try {
			const bundle = await createIdentityRequest(context, this.buoyUrl);
			if (!webUrl) {
				preopened?.close();
				return await this.native.login(this.data, context, bundle, t);
			}
			const forcedMode = perCall.mode ?? this.loginModeOverride;
			if (forcedMode) {
				writeMode(this.data, forcedMode);
				return await this.loginWithSwitch(context, bundle, t, webUrl, forcedMode, true, preopened);
			}
			const { mode, popup } = await this.chooseMode(context, bundle, t, webUrl, preopened);
			writeMode(this.data, mode);
			if (mode === "web") return await this.web.login(this.data, context, bundle, webUrl, popup);
			return await this.loginWithSwitch(context, bundle, t, webUrl, "app", false);
		} catch (error) {
			preopened?.close();
			if (!(error instanceof AnchorRequestCancelledError)) throw error;
			return this.recoverLogin(context, t, webUrl);
		}
	}
	async recoverLogin(context, t, webUrl) {
		for (;;) {
			const bundle = await createIdentityRequest(context, this.buoyUrl);
			const { mode, popup } = await this.chooseRecoveryMode(context, bundle, t, webUrl);
			writeMode(this.data, mode);
			try {
				return mode === "web" && webUrl ? await this.web.login(this.data, context, bundle, webUrl, popup) : await this.native.login(this.data, context, bundle, t);
			} catch (error) {
				if (!(error instanceof AnchorRequestCancelledError)) throw error;
			}
		}
	}
	async loginWithSwitch(context, bundle, t, webUrl, mode, immediate, preopened) {
		if (preopened) {
			if (mode === "web") preopened.location.href = this.web.loginUrl(context, bundle, webUrl);
			else {
				preopened.close();
				preopened = void 0;
			}
		}
		const alternateBundle = await createIdentityRequest(context, this.buoyUrl);
		const alternateUrl = this.web.loginUrl(context, alternateBundle, webUrl);
		let popup = null;
		let switchTo = () => void 0;
		const switched = new Promise((resolve) => {
			switchTo = () => {
				if (mode === "app") popup = this.web.openWindow(alternateUrl);
				resolve("switched");
			};
		});
		const primary = mode === "app" ? this.native.login(this.data, context, bundle, t, {
			immediate,
			delayMs: this.webFallbackDelayMs,
			onSelect: () => switchTo()
		}) : this.web.login(this.data, context, bundle, webUrl, preopened, { onSelect: () => switchTo() });
		primary.catch(() => void 0);
		if (await Promise.race([primary.then(() => "primary"), switched]) === "primary") return primary;
		const next = mode === "app" ? "web" : "app";
		writeMode(this.data, next);
		return next === "web" ? this.web.login(this.data, context, alternateBundle, webUrl, popup) : this.native.login(this.data, context, alternateBundle, t);
	}
	chooseMode(context, bundle, t, webUrl, preopened) {
		return new Promise((resolve, reject) => {
			const url = this.web.loginUrl(context, bundle, webUrl);
			promptForMode(context, t, (mode) => {
				if (mode === "web") {
					if (preopened) {
						preopened.location.href = url;
						resolve({
							mode,
							popup: preopened
						});
					} else resolve({
						mode,
						popup: this.web.openWindow(url)
					});
				} else {
					preopened?.close();
					resolve({ mode });
				}
			}).catch(reject);
		});
	}
	chooseRecoveryMode(context, bundle, t, webUrl) {
		return new Promise((resolve, reject) => {
			promptForRecovery(context, t, Boolean(webUrl), (mode) => {
				if (mode === "web" && webUrl) {
					const url = this.web.loginUrl(context, bundle, webUrl);
					resolve({
						mode,
						popup: this.web.openWindow(url)
					});
				} else resolve({ mode });
			}).catch(reject);
		});
	}
	sign(resolved, context) {
		return this.handleSign(resolved, context);
	}
	async handleSign(resolved, context) {
		if ((readMode(this.data) || "app") === "web") {
			const webUrl = resolveWebAuthenticatorUrl(context.chain?.id, this.webAuthenticatorUrls);
			if (!webUrl) throw new Error(`This session signs in the browser, but there is no Anchor web authenticator for chain ${context.chain?.id}.`);
			return this.web.sign(this.data, resolved, context, webUrl);
		}
		return this.native.sign(this.data, resolved, context);
	}
};
//#endregion
export { DEFAULT_WEB_AUTHENTICATOR_URLS, DEFAULT_WEB_FALLBACK_DELAY_MS, WalletPluginAnchor };

//# sourceMappingURL=wallet-plugin-anchor.m.js.map